/**
 * SteadyPaceSkillEffect — Specter Phantom/Orchestrate terminal passive.
 *
 * Walking recharges stamina at full rate (1.0x instead of the default 0.5x).
 * This replaces the old Conductor terminal, giving Orchestrate-path players
 * full stamina recovery even while moving — no need to stand still.
 *
 * On equip: sets `walkingRechargeRate` on StaminaComponent to 1.0.
 * On unequip: resets `walkingRechargeRate` to 0.5 (default).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-080 (terminal passive pattern)
 * @see specter-drift-path-proposal.md § Steady Pace
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SteadyPaceDeps {
  readonly stamina: {
    /** Walking recharge rate multiplier (default 0.5). */
    walkingRechargeRate: number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SteadyPaceConfig {
  /** Walking recharge rate multiplier to apply (1.0 = full rate). */
  readonly walkingRechargeRate: number;
}

const DEFAULT_CONFIG: SteadyPaceConfig = {
  walkingRechargeRate: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SteadyPaceSkillEffect implements SkillEffect {
  readonly skillId = 'steady_pace';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: SteadyPaceDeps;
  private readonly config: SteadyPaceConfig;

  /** Whether the rate has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: SteadyPaceDeps, config: Partial<SteadyPaceConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    if (!this._applied) {
      this.deps.stamina.walkingRechargeRate = this.config.walkingRechargeRate;
      this._applied = true;
    }
  }

  destroy(): void {
    if (this._applied) {
      this.deps.stamina.walkingRechargeRate = 0.5;
      this._applied = false;
    }
  }
}
